// Daily usage data for a single app
export interface DailyAppUsage {
  packageName: string;
  appName?: string;
  duration: number; // minutes
  sessions: number;
  lastUsed?: Date;
}

// Daily usage summary for a specific date
export interface DailyUsageSummary {
  date: Date;
  dateString: string; // YYYY-MM-DD
  totalScreenTime: number; // minutes
  apps: DailyAppUsage[];
}

// Weekly usage data
export interface WeeklyUsageData {
  week: {
    start: Date;
    end: Date;
  };
  daily: DailyUsageSummary[];
  total: number; // total minutes for the week
  averageDaily: number;
}

// Block event with metadata
export interface BlockEventData {
  id: string;
  packageName: string;
  appName?: string;
  reason: 'schedule' | 'limit_reached' | 'blocklist';
  timestamp: Date;
  rule?: string; // name of blocking rule (e.g., "Bedtime", "App Limit")
}

// Block statistics
export interface BlockStats {
  totalBlocks: number;
  byReason: {
    schedule: number;
    limit_reached: number;
    blocklist: number;
  };
  byApp: Record<string, number>;
  recent: BlockEventData[];
}

// Trend data comparing two periods
export interface UsageTrend {
  appName: string;
  packageName: string;
  previous: number; // minutes
  current: number; // minutes
  change: number; // percentage change
  delta: number; // absolute change in minutes
  trend: 'up' | 'down' | 'stable';
}

// Prediction data for daily limit
export interface DailyLimitPrediction {
  currentUsage: number; // minutes used so far today
  dailyLimit: number; // minutes limit
  percentUsed: number; // percentage of limit used
  estimatedTotalUsage: number; // estimated usage by end of day
  willExceed: boolean;
  minutesUntilLimit: number;
  estimatedTimeUntilLimit: Date | null; // when will limit be exceeded
}

// Full analytics dashboard state
export interface UsageAnalyticsState {
  loading: boolean;
  error: string | null;
  today: DailyUsageSummary | null;
  week: WeeklyUsageData | null;
  blockStats: BlockStats | null;
  trends: UsageTrend[];
  prediction: DailyLimitPrediction | null;
  selectedApp?: string; // for detailed app view
  dateRange: {
    start: Date;
    end: Date;
  };
}

// Helper: format date as YYYY-MM-DD
export function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Helper: parse date from YYYY-MM-DD
export function parseDateKey(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Helper: format minutes as HH:MM or Xm
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${Math.round(mins)}m`;
}

// Helper: get start of week (Sunday)
export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
}

// Helper: get end of week (Saturday)
export function getWeekEnd(date: Date): Date {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return end;
}

// Helper: get yesterday's date
export function getYesterdayDate(): Date {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
}

// Helper: calculate trend
export function calculateTrend(previous: number, current: number): UsageTrend['trend'] {
  if (current > previous * 1.1) return 'up';
  if (current < previous * 0.9) return 'down';
  return 'stable';
}
